// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryItem from "@tps/App/Components/Categories/CategoryItem";
import TitleTypography from "@tps/App/Components/Common/TitleTypography";
import XngCarousel, { SwiperStyle } from "@tps/App/Components/XngCarousel";
import loadFeaturedCategories from "@tps/App/Handlers/Categories/LoadFeaturedCategories";
import { CustomAppState } from "@tps/App/Store/Reducers";
import {
    FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE,
    SLIDES_PER_VIEW_POINTS,
    XngCustomGroup,
} from "@tps/XDL/constants";
import { color, xScreen } from "@tps/XDL/Utilities";
import { generateSlidesPerViewMap } from "@tps/XDL/Utilities";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    viewAllLink = "viewAllLink",
    selectedCategoryIds = "selectedCategoryIds",
    showAsCarousel = "showAsCarousel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: any[];
        [fields.viewAllLink]: LinkFieldValue;
        [fields.selectedCategoryIds]: string[];
        [fields.showAsCarousel]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        categories: state.xFeaturedCategories.data,
        isLoading: state.xFeaturedCategories.isLoading,
        isMobileScreen: state.xCommon?.isMobileScreen,
    };
};
const mapDispatchToProps = {
    loadFeaturedCategories,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface FeaturedCategoriesStyles {
    link?: LinkProps;
    wrapper?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    categoryCardsGridContainer?: GridContainerProps;
    categoryCardGridItem?: GridItemProps;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {
    wrapper: {
        css: css`
            padding: 32px 0;
            .swiper-scrollbar.swiper-scrollbar-horizontal {
                background-color: ${color("common.ui3")};
            }
        `,
    },
};

const titleTypoStyle = {
    wrapper: {
        css: css`
            margin-bottom: 16px;
        `,
    },
};

const styles = featuredCategoriesStyles;

const FeaturedCategories: React.FC<Props> = ({
    fields: { title, viewAllLink, selectedCategoryIds, showAsCarousel },
    isLoading,
    categories,
    isMobileScreen,
    loadFeaturedCategories,
}: Props) => {
    useEffect(() => {
        loadFeaturedCategories({ categoriesIds: selectedCategoryIds });
    }, [selectedCategoryIds]);

    const viewAllLinkUrl = useGetLink(viewAllLink)?.url;

    if (!categories || !categories.length || isLoading) {
        return null;
    }

    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.mobile,
        [SLIDES_PER_VIEW_POINTS.sm]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.tablet,
        [SLIDES_PER_VIEW_POINTS.md]: FEATURED_CATEGORIES_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
        [SLIDES_PER_VIEW_POINTS.lg]: parseInt("5", 10),
    });

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="featuredCategoriesWidget">
            {title && (
                <TitleTypography
                    link={viewAllLinkUrl}
                    linkText={translate("View All Categories")}
                    data-test-selector="featuredCategoriesTitle"
                    extendedStyles={titleTypoStyle}
                >
                    {title}
                </TitleTypography>
            )}

            {showAsCarousel ? (
                <StyledWrapper {...styles.carouselSlidesContainer}>
                    <XngCarousel
                        spaceBetween={isMobileScreen ? 16 : 22}
                        scrollbar={true}
                        navigation={true}
                        pagination={false}
                        slidesPerView={2}
                        breakpoints={breakpoints}
                    >
                        {categories?.map((item: any) => (
                            <CategoryItem key={item.id} category={item} categoryImage={item.largeImagePath} />
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            ) : (
                <GridContainer {...styles.categoryCardsGridContainer} gap={22}>
                    {categories?.map(category => {
                        return (
                            <GridItem key={category.id} {...styles.categoryCardGridItem}>
                                <CategoryItem category={category} categoryImage={category.largeImagePath} />
                            </GridItem>
                        );
                    })}
                </GridContainer>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedCategories),
    definition: {
        group: XngCustomGroup,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.viewAllLink,
                displayName: "View All Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Select Category",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                validate: value => (value.length > 15 ? translate("You Can Add 15 Categories Only") : null),
            },
            {
                name: fields.showAsCarousel,
                displayName: "Show As Carousel",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
